import { useState, useEffect, useRef } from 'react';

// Define the event type
interface ForceAnimationEvent extends CustomEvent {
  detail: {
    timestamp: number;
  };
}

/**
 * Hook to handle forced animations across components
 * @param {boolean} inView - The result from useInView hook
 * @returns {boolean} - Whether the component should be animated
 */
export const useForceAnimation = (inView: boolean): boolean => {
  const [forceVisible, setForceVisible] = useState(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleForceAnimations = (e: Event) => {
      const customEvent = e as ForceAnimationEvent;
      console.log("Force animation event received in hook", customEvent.detail);
      setForceVisible(true);

      // Clear any existing timeout
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }

      // Set a new timeout to reset forceVisible as a fallback
      timeoutIdRef.current = setTimeout(() => {
        console.log("Resetting forceVisible due to timeout");
        setForceVisible(false);
        timeoutIdRef.current = null;
      }, 1000); // Shortened fallback timeout, e.g., 1000ms
    };

    window.addEventListener('forceAnimations', handleForceAnimations);

    return () => {
      window.removeEventListener('forceAnimations', handleForceAnimations);
      // Clear timeout on unmount
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []); // Empty dependency array, sets up listener once

  // Effect to reset forceVisible if the component comes into view
  useEffect(() => {
    if (inView && forceVisible) {
      console.log("Resetting forceVisible because component is inView");
      setForceVisible(false);
      // Clear the fallback timeout as well, since it's no longer needed
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    }
  }, [inView, forceVisible]); // Runs when inView or forceVisible changes

  return inView || forceVisible;
};